import { createFeatureSelector, createSelector } from '@ngrx/store';
import { submittalsFeatureKey, SubmittalsState } from './submittals.reducer';
import {
  SubmittalPackage,
  SubmittalPackageResp,
  SubmittalPackageListingItem,
  SubmittalPackageSentFor,
  SubmittalPackageTableData,
  SubmittalRegistry,
  SUBMITTAL_REGISTRY_STATUS,
} from '@app/store/submittals/submittals.types';

// Feature selector
const selectSubmittalsState = createFeatureSelector<SubmittalsState>(submittalsFeatureKey);

// Submittal Packages Selectors
const selectPackages = createSelector(selectSubmittalsState, (state) => state.packages);

const selectPackageTableData = createSelector(selectPackages, (packages) =>
  packages.map((submittalPackage: SubmittalPackageListingItem): SubmittalPackageTableData => {
    return {
      ...submittalPackage,
      package_approver: {
        ...submittalPackage.package_approver,
      },
      package_manager: {
        ...submittalPackage.package_manager,
      },
    };
  }),
);

const getSentFor = (packageDetails: SubmittalPackageResp): SubmittalPackageSentFor => {
  if (packageDetails.sent_to_owner_at) {
    return 'Owner';
  } else if (packageDetails.requested_approval_at && !packageDetails.received_approval_at) {
    return 'Approval';
  } else if (packageDetails.requested_submittals_at && !packageDetails.received_submittals_at) {
    return 'Submittals';
  }
  return null;
};

/**
 * Dynamically map submittal registry entries' status if staus is missing. <br/>
 * The status is stored as NULL on backend when the submittal is requested but not yet sent/received.
 * @param packageDetails
 */
const getMappedEntries = (packageDetails: SubmittalPackageResp): SubmittalRegistry[] => {
  if (!packageDetails.entries || packageDetails.entries.length === 0) {
    return [];
  }
  const requestedSubmittals = !!packageDetails.requested_submittals_at;
  const receivedSubmittals = !!packageDetails.received_submittals_at;
  const role = packageDetails.role;

  return packageDetails.entries.map((entry) => {
    let status = entry.status;

    if (!status && role === 'manager') {
      if (requestedSubmittals && !receivedSubmittals) {
        status = SUBMITTAL_REGISTRY_STATUS.NOT_RECEIVED;
      } else if (receivedSubmittals) {
        status = SUBMITTAL_REGISTRY_STATUS.RECEIVED;
      }
    } else if (!status && requestedSubmittals && role === 'owner') {
      status = SUBMITTAL_REGISTRY_STATUS.NOT_SENT;
    }

    return {
      ...entry,
      status,
    };
  });
};

const getExtendedPackageDetails = (packageDetails: SubmittalPackageResp): SubmittalPackage => {
  if (!packageDetails) {
    return null;
  }

  const sent_for = getSentFor(packageDetails);
  const entries = getMappedEntries(packageDetails);

  return {
    ...packageDetails,
    entries,
    sent_for,
  };
};

const selectPackageDetails = createSelector(selectSubmittalsState, (state) =>
  getExtendedPackageDetails(state.packageDetails),
);

const selectPackageDetailsLoading = createSelector(
  selectSubmittalsState,
  (state) => state.packageDetailsLoading,
);

const selectPackageRegistryEntriesLoading = createSelector(
  selectSubmittalsState,
  (state) => state.packageRegistryEntriesLoading,
);

const selectPackagesListLoading = createSelector(
  selectSubmittalsState,
  (state) => state.packagesListLoading,
);

const selectPackagesListFilters = createSelector(
  selectSubmittalsState,
  (state) => state.packagesListFilters,
);

// Submittal Registry Selectors
const selectAllSubmittalRegistries = createSelector(
  selectSubmittalsState,
  (state) => state.submittalRegistries,
);

const selectSubmittalRegistriesLoading = createSelector(
  selectSubmittalsState,
  (state) => state.registriesLoading,
);

const selectSubmittalRegistriesError = createSelector(
  selectSubmittalsState,
  (state) => state.registriesError,
);

const selectSubmittalRegistriesFilters = createSelector(
  selectSubmittalsState,
  (state) => state.registriesFilters,
);

const selectNextSubmittalRegistryId = createSelector(selectSubmittalsState, (state) => {
  return state.nextRegistryId;
});

const selectNextSubmittalRegistryIdIsLoading = createSelector(selectSubmittalsState, (state) => {
  return state.nextRegistryIdIsLoading;
});

export const submittalsSelectors = {
  // State
  selectSubmittalsState,

  // Package selectors
  selectPackageTableData,
  selectPackagesListLoading,
  selectPackagesListFilters,

  // Submittal Package details
  selectPackageDetails,
  selectPackageDetailsLoading,
  selectPackageRegistryEntriesLoading,

  // Registry selectors
  selectAllSubmittalRegistries,
  selectSubmittalRegistriesLoading,
  selectSubmittalRegistriesError,
  selectSubmittalRegistriesFilters,

  selectNextSubmittalRegistryId,
  selectNextSubmittalRegistryIdIsLoading,
};
